<template>
	<label class="input-avatar">
		<!-- replace with uploaded image when present -->
		<div v-if="filePreviewUrl" class="preview">
			<img :src="filePreviewUrl" alt="Preview" />
		</div>

		<div v-else class="placeholder">{{ defaultProfileIcon }}</div>

		<span v-if="!fileAllowed.valid" class="error">Not Allowed: {{ fileAllowed.error }}</span>

		<button type="button" class="select" @click="openFileSelector">
			<FontAwesomeIcon :icon="faPencil" />
		</button>
	</label>
</template>

<script setup lang="ts">
import { useFileDialog } from '@vueuse/core';

import { computed, ref, watch } from 'vue';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';

const props = withDefaults(
	defineProps<{
		/** Maximum allowed file size in mb. Default is 5mb. */
		fileSizeLimit?: number;
	}>(),
	{ fileSizeLimit: 5 }
);

const emit = defineEmits<{
	(e: 'fileUpdated', base64: string): void;
	(e: 'open'): void;
}>();

const { profileInfo, userInfo } = storeToRefs(useUserStore());
const defaultProfileIcon = computed(() =>
	(profileInfo.value?.firstName || userInfo.value.email)?.charAt(0).toUpperCase()
);

const base64Image = ref<string | null>(null);

const { files, open } = useFileDialog({
	multiple: false,
	accept: 'image/jpeg, image/jpg, image/png, image/avif, image/webp',
	reset: true,
});

function openFileSelector() {
	open();
	emit('open');
}

const filePreviewUrl = computed(() => {
	if (files.value?.[0]) {
		return URL.createObjectURL(files.value[0]);
	}

	if (profileInfo.value?.avatarUrl) {
		return profileInfo.value?.avatarUrl;
	}

	if (userInfo.value.photoURL) {
		return userInfo.value.photoURL;
	}

	return '';
});

const fileAllowed = computed(() => {
	if (files.value == null) return { error: 'no files', valid: true };
	if (files.value.length === 0) return { error: null, valid: true };
	if (files.value.length > 1) return { error: 'max 1 file', valid: false };

	const file = files.value.item(0);
	if (file == null) return { error: 'file error', valid: false };
	if (file.name == null) return { error: 'no filename', valid: false };

	const fileSize = Number((file.size / 1024 / 1024).toFixed(4));
	if (Number.isNaN(fileSize)) return { error: 'file size', valid: false };
	if (fileSize > props.fileSizeLimit) return { error: `file larger than ${props.fileSizeLimit}mb`, valid: false };

	return { error: null, valid: true };
});

const encodeImageFileAsURL = (file: File): Promise<string | ArrayBuffer | null> => {
	return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
		if (!file) {
			reject('No file provided');
			return;
		}

		const reader: FileReader = new FileReader();
		reader.onloadend = () => resolve(reader.result);
		reader.onerror = () => reject('Error reading file');
		reader.readAsDataURL(file);
	});
};

watch(files, async newFiles => {
	if (newFiles && newFiles[0]) {
		try {
			const encodedImage = (await encodeImageFileAsURL(newFiles[0])) as string;
			base64Image.value = encodedImage.substring(encodedImage.indexOf(',') + 1);
			emit('fileUpdated', base64Image.value);
		} catch (error) {
			console.error(error);
			base64Image.value = null;
		}
	} else {
		base64Image.value = null;
	}
});
</script>

<style scoped lang="scss">
.input-avatar {
	position: relative;
	display: inline-grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr;
	place-items: center;
	cursor: pointer;
	width: min-content;

	.preview {
		width: 56px;
		height: 56px;
		border-radius: 100%;
		overflow: hidden;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 28px;
		font-weight: 900;

		width: 56px;
		height: 56px;
		border-radius: 100%;
		color: white;
		background-color: #fbc500;
	}

	.error {
		color: tomato;
	}

	.select {
		all: unset;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		color: white;
		background-color: #232932;

		border-radius: 100%;
		height: 24px;
		width: 24px;

		position: absolute;
		inset-inline-end: 0;
		bottom: 0;
		transform: translate(9px, 9px);

		svg {
			width: 12px;
			height: 12px;
		}
	}
}
</style>
